import React, { useState, useRef } from 'react';
import { AudioOutlined, CloseOutlined } from '@ant-design/icons';
import './audioRecording.less';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { LiveAudioVisualizer } from 'react-audio-visualize';

interface RecordingPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onRecordingComplete: (audioFile: File, duration: string) => void;
}

const RecordingPopup: React.FC<RecordingPopupProps> = ({
  isOpen,
  onClose,
  onRecordingComplete,
}) => {
  const [recordingState, setRecordingState] = useState<
    'initial' | 'recording' | 'review' | 'save'
  >('initial');
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [fileName, setFileName] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerIntervalRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const togglePlayback = () => {
    if (!audioUrl) return;

    if (!audioRef.current) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.addEventListener('ended', () => setIsPlaying(false));
    }

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch((error) => {
        console.error('Error playing audio:', error);
        setIsPlaying(false);
      });
    }
    setIsPlaying(!isPlaying);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/mpeg',
        });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        setRecordingState('review');
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };

      mediaRecorder.start();
      setRecordingState('recording');
      setRecordingTime(0);

      timerIntervalRef.current = window.setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
    }
  };

  const handleSave = () => {
    if (!audioUrl || !fileName) return;

    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/mpeg' });
    const audioFile = new File([audioBlob], `${fileName}.mp3`, {
      type: 'audio/mpeg',
    });
    onRecordingComplete(audioFile, formatTime(recordingTime));
    resetState();
    onClose();
  };

  const resetState = () => {
    setRecordingState('initial');
    setRecordingTime(0);
    setFileName('');
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="recording-popup">
      <div className="recording-popup-content">
        {recordingState === 'initial' && (
          <>
            <div className="recording-button" onClick={startRecording}>
              <AudioOutlined />
            </div>
            <span>Press button to start recording</span>
          </>
        )}

        {recordingState === 'recording' && (
          <div className="recording-state">
            <div className="recording-controls">
              <div
                className="recording-button recording"
                onClick={stopRecording}
              >
                <div className="pause-icon" />
              </div>
              <div className="recording-time">{formatTime(recordingTime)}</div>
            </div>
            <div className="waveform-container">
              {mediaRecorderRef.current && (
                <LiveAudioVisualizer
                  mediaRecorder={mediaRecorderRef.current}
                  width={250}
                  height={60}
                />
              )}
            </div>
          </div>
        )}

        {recordingState === 'review' && (
          <div className="review-state">
            <div className="audio-player">
              <div className="playback-controls">
                <button className="play-button" onClick={togglePlayback}>
                  {!isPlaying ? <PlayArrowIcon /> : <PauseIcon />}
                </button>
                <div className="recording-time">
                  {formatTime(recordingTime)}
                </div>
              </div>
            </div>
            <div className="review-controls">
              <input
                type="text"
                placeholder="Enter file name"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                className="filename-input-save"
              />
              <div className="review-actions">
                <button
                  className="save-button"
                  onClick={handleSave}
                  disabled={!fileName}
                >
                  Save to timeline
                </button>
                <button
                  className="discard-button"
                  onClick={() => {
                    resetState();
                  }}
                  title="Discard"
                >
                  <CloseOutlined />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordingPopup;
